import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "../ui/button";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const optionMatches = (option, query) => `${option.label} ${option.searchText || ""}`.toLowerCase().includes(query.toLowerCase());

export const SearchableSelect = ({
  dataTestId,
  disabled = false,
  emptyLabel = "No results found.",
  onChange,
  options,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  triggerClassName = "",
  value,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = useMemo(() => options.find((option) => option.value === value) || null, [options, value]);
  const filteredOptions = useMemo(() => options.filter((option) => optionMatches(option, query)), [options, query]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button className={`w-full justify-between rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-sm font-normal text-zinc-900 hover:bg-white ${triggerClassName}`} data-testid={dataTestId} disabled={disabled} title={selectedOption?.label || placeholder} type="button" variant="outline">
          <span className="truncate text-left">{selectedOption?.label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0" data-testid={`${dataTestId}-popover`}>
        <Command>
          <CommandInput data-testid={`${dataTestId}-search`} onValueChange={setQuery} placeholder={searchPlaceholder} value={query} />
          <CommandList className="max-h-64 overscroll-contain overflow-y-auto" onWheelCapture={(event) => event.stopPropagation()}>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            {filteredOptions.map((option) => (
              <CommandItem data-testid={`${dataTestId}-option-${option.value}`} key={option.value} onSelect={() => {
                onChange(option.value);
                setQuery("");
                setOpen(false);
              }} value={`${option.label} ${option.searchText || ""}`}>
                <Check className={`h-4 w-4 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
                <div className="min-w-0">
                  <p className="truncate">{option.label}</p>
                  {option.meta ? <p className="truncate text-xs text-zinc-500">{option.meta}</p> : null}
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};